import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  User,
  UserCreateRequest,
  UserUpdateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';

type UserRow = User & {
  isActive: boolean;
};

function toNumberOrNull(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '' || value === 'null') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
}

function toVersionString(value: unknown): string | null | undefined {
  if (value === undefined || value === null || typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' && Number.isSafeInteger(value)) {
    return String(value);
  }

  throw new TypeError('Version must be a string or safe integer.');
}

function buildCreateRequest(data: Record<string, unknown>): UserCreateRequest {
  return {
    ...data,
    allotmentRoleId: toNumberOrNull(data.allotmentRoleId),
    tourOperatorId: toNumberOrNull(data.tourOperatorId),
    tourOperatorRoleId: toNumberOrNull(data.tourOperatorRoleId),
  } as UserCreateRequest;
}

function buildUpdateRequest(data: Record<string, unknown>): UserUpdateRequest {
  return {
    ...data,
    allotmentRoleId: toNumberOrNull(data.allotmentRoleId),
    id: toNumberOrNull(data.id),
    tourOperatorId: toNumberOrNull(data.tourOperatorId),
    tourOperatorRoleId: toNumberOrNull(data.tourOperatorRoleId),
    version: toVersionString(data.version),
  } as UserUpdateRequest;
}

function mapUsers(data: User[] | undefined, isActive: boolean): UserRow[] {
  return (data ?? []).map(entry => ({
    ...entry,
    isActive,
  }));
}

function buildAbstract(data: UserRow[]): Record<string, string> {
  return Object.fromEntries(
    data.map(entry => [
      String(entry.id ?? ''),
      `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim(),
    ])
  );
}

export const useUsersStore = defineStore('users', () => {
  const apiClient = useApiClient();

  const users = ref<UserRow[]>([]);
  const user = ref<User | null>(null);
  const abstract = ref<Record<string, string> | null>(null);
  const accessToken = ref<string | null>(null);
  const profile = ref<User | null>(null);
  const loading = ref<boolean>(true);

  function getUserById(id: number): UserRow | undefined {
    return users.value.find(currentUser => currentUser.id === id);
  }

  function getUsernameById(id: number): string | null {
    const selectedUser = getUserById(id);
    return selectedUser ? `${selectedUser.firstName ?? ''} ${selectedUser.lastName ?? ''}`.trim() : null;
  }

  async function add(data: Record<string, unknown>): Promise<User | undefined> {
    const response = await apiClient.user.post(buildCreateRequest(data));
    user.value = response ?? null;
    return response;
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.user.byId(id).byIsActive(active).post();
  }

  async function update(data: Record<string, unknown>): Promise<ArrayBuffer | undefined> {
    return await apiClient.user.put(buildUpdateRequest(data));
  }

  async function get({ showOnlyActive = true }: { showOnlyActive?: boolean } = {}): Promise<UserRow[]> {
    loading.value = true;

    try {
      const response = await apiClient.user.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      users.value = mapUsers(response, showOnlyActive);
      abstract.value = buildAbstract(users.value);

      return users.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    users,
    user,
    abstract,
    accessToken,
    profile,
    loading,
    add,
    toggle,
    update,
    get,
    getUserById,
    getUsernameById,
  };
});
